import { useEffect, useRef, type ReactNode } from 'react';
import { passthrough } from './passthrough.js';
import { cx } from './cx.js';
import { Button } from './Button.tsx';
import { Icon } from './Icon.tsx';

/**
 * ConfirmDialog - confirm a consequential action.
 *
 * Three things it insists on.
 *
 * **The button says what it will do.** Not "OK". A person who has read nothing
 * else reads the button, and "Delete 4 views" is a different decision from
 * "Confirm".
 *
 * **What will happen goes in the body, not in the title.** The title names the
 * action; the body enumerates the consequence. A dialog that puts the
 * consequence in a title has nowhere to put the detail, so it omits it.
 *
 * Its body is marked `ImpactPreview`, which is what 06_Component_Library calls
 * the thing that says what a confirmation will do. Every consequential dialog
 * in this product has one, because a dialog whose body is "Are you sure?" is a
 * request to guess.
 *
 * **Focus starts on the safe control.** Opening a destructive dialog with the
 * destructive button focused turns a stray Enter into a deletion.
 */

export interface ConfirmDialogLabels {
  cancel: string;
}

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  /** What will happen, enumerated. Never a restatement of the title. */
  children: ReactNode;
  /** The label is the decision: "Delete 4 views", not "Confirm". */
  confirmLabel: string;
  confirmIntent?: 'primary' | 'danger';
  labels: ConfirmDialogLabels;
  onConfirm: () => void;
  onCancel: () => void;
  busy?: boolean;
  /** Blocks confirmation and says why, rather than a disabled button in silence. */
  blockedReason?: string | undefined;
}

export function ConfirmDialog({
  open,
  title,
  children,
  confirmLabel,
  confirmIntent = 'primary',
  labels,
  onConfirm,
  onCancel,
  busy,
  blockedReason,
  ...rest
}: ConfirmDialogProps & Record<string, unknown>): JSX.Element | null {
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    // The safe control, not the consequential one: a stray Enter on an open
    // dialog must not be the thing that deletes something.
    if (open) cancelRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  if (!open) return null;

  return (
    <div
      {...passthrough(rest)}
      data-component="ConfirmDialog"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className={cx(
        'flex w-full max-w-modal-confirm flex-col gap-3 rounded-card bg-surface p-4',
        'shadow-modal',
      )}
    >
      <h2 className="text-h2 text-ink">{title}</h2>
      <div data-component="ImpactPreview" className="text-body text-ink-meta">
        {children}
      </div>

      {blockedReason ? (
        <p role="alert" className="flex items-start gap-2 text-dense text-warning-text">
          <Icon name="Warning" size="inline" decorative />
          {blockedReason}
        </p>
      ) : null}

      <div className="flex flex-wrap items-center justify-end gap-2">
        <Button ref={cancelRef} intent="ghost" onClick={onCancel}>
          {labels.cancel}
        </Button>
        <Button
          intent={confirmIntent}
          data-confirm
          disabled={busy || Boolean(blockedReason)}
          onClick={onConfirm}
        >
          {confirmLabel}
        </Button>
      </div>
    </div>
  );
}
